import {
  MultipleProductCard,
  MultipleProductCardVariant,
  SingularProductCard,
  SingularProductCardVariant
} from "@shared/graphql.types";

import {
  ActivePriceEnum,
  ProductCardDataType
} from "../components/VerticalProductCard/components/types";

import { generateProductCardHref } from "./generateProductCardHref";

export const isMultipleProductCard = (
  card?: SingularProductCard | MultipleProductCard
): card is MultipleProductCard => {
  return card?.type === "multivariate";
};

export const getButtonText = (
  product: SingularProductCard | MultipleProductCard,
  variant: SingularProductCardVariant | MultipleProductCardVariant
) => {
  if (!variant.prices?.activeType) {
    return product.buttonText;
  }

  const activePricing = variant.prices[variant.prices.activeType];
  return (
    activePricing?.promo?.label ||
    activePricing?.regular?.label ||
    product.buttonText
  );
};

export const toProductCardData = (
  productCard: MultipleProductCard | SingularProductCard,
  activeShade = 0,
  inWishlist = false,
  hasMembershipInBag = false,
  ctaButtonAction: "addToBasket" | "selectShade" = "addToBasket"
) => {
  let productCardData: ProductCardDataType;
  const variant = productCard.variants[activeShade];
  const href = generateProductCardHref(variant.href);

  // Create a modified variant with the correct activeType based on membership logic
  const effectiveActiveType = hasMembershipInBag
    ? "member"
    : (variant.prices?.activeType ?? "guest");

  const variantWithCorrectActiveType = {
    ...variant,
    prices: variant.prices
      ? {
          ...variant.prices,
          activeType: effectiveActiveType
        }
      : variant.prices
  };

  const commonProductData = {
    id: productCard.id,
    buttonText:
      isMultipleProductCard(productCard) && ctaButtonAction === "selectShade"
        ? "Select Shade"
        : getButtonText(productCard, variantWithCorrectActiveType),
    brandName: productCard.brandName ?? undefined,
    productName: productCard.title,
    href,
    inStock: variant.availability.inStock,
    primaryPrice: variant.pricing.primary.formatted,
    primaryPriceRaw: variant.pricing.primary.raw,
    primarySubtitle: variant.pricing.primary.label,
    secondaryPriceRaw: variant.pricing.secondary.raw,
    secondaryPrice: variant.pricing.secondary.formatted,
    secondarySubtitle: variant.pricing.secondary.label,
    rating: productCard.reviews.stars,
    reviewCount: productCard.reviews.count,
    inWishlist: inWishlist,
    badge: variant.badge
      ? {
          src: variant.badge.src,
          // TODO: description was changed updated from required to optional by CSE team
          alt: variant.badge.description ?? ""
        }
      : undefined,
    ribbon: variant.ribbon
      ? {
          ribbonKey: variant.ribbon.key,
          text: variant.ribbon.text
        }
      : undefined,
    image: {
      alt:
        productCard?.variants?.[activeShade]?.images?.[0]?.altText ?? undefined,
      src: productCard?.variants?.[activeShade]?.images?.[0]?.src
    },
    sku: variant.sku,
    variationName: variant.shade ?? "",
    highlightedPrice: productCard.highlightedPrice ?? "primary",
    prices: {
      activeType: effectiveActiveType as ActivePriceEnum,
      member: {
        regular: {
          formatted: variant.prices?.member.regular.formatted ?? "",
          raw: variant.prices?.member.regular.raw ?? 0,
          label: variant.prices?.member.regular.label ?? "",
          currency: variant.prices?.member.regular.currency ?? "",
          currencySymbol: variant.prices?.member.regular.currencySymbol ?? ""
        },
        promo: variant.prices?.member?.promo
          ? {
              formatted: variant.prices?.member?.promo?.formatted,
              raw: variant.prices?.member?.promo?.raw,
              label: variant.prices?.member?.promo?.label,
              currency: variant.prices?.member?.promo?.currency,
              currencySymbol: variant.prices?.member?.promo?.currencySymbol
            }
          : undefined
      },
      guest: {
        regular: {
          formatted: variant.prices?.guest.regular.formatted ?? "",
          raw: variant.prices?.guest.regular.raw ?? 0,
          label: variant.prices?.guest.regular.label ?? "",
          currency: variant.prices?.guest.regular.currency ?? "",
          currencySymbol: variant.prices?.guest.regular.currencySymbol ?? ""
        },
        promo: variant.prices?.guest?.promo
          ? {
              formatted: variant.prices?.guest?.promo?.formatted,
              raw: variant.prices?.guest?.promo?.raw,
              label: variant.prices?.guest?.promo?.label,
              currency: variant.prices?.guest?.promo?.currency,
              currencySymbol: variant.prices?.guest?.promo?.currencySymbol
            }
          : undefined
      },
      typical: {
        regular: {
          formatted: variant.prices?.typical.regular.formatted ?? "",
          raw: variant.prices?.typical.regular.raw ?? 0,
          label: variant.prices?.typical.regular.label ?? "",
          currency: variant.prices?.typical.regular.currency ?? "",
          currencySymbol: variant.prices?.typical.regular.currencySymbol ?? ""
        }
      }
    }
  };
  if (isMultipleProductCard(productCard)) {
    productCardData = {
      ...commonProductData,
      swatchImages: productCard.swatchImages ?? [],
      isPrimaryButtonDisabled: !variant.availability.inStock
    };
    return productCardData;
  }
  return {
    ...commonProductData,
    isPrimaryButtonDisabled: !variant.availability.inStock
  };
};
